import { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import Chat from './Chat';
import ChatLauncher from './ChatLauncher';
import './Chatbot.css';

interface SupportWidgetProps {
  apiBase?: string;
  brandColor?: string;
  zIndex?: number;
  /** When true, hide the floating launcher (e.g. on Workbench where Support is in the header) */
  hideLauncher?: boolean;
}

/**
 * SupportWidget
 * - Non-invasive portal wrapper that renders the existing Chat UI into a fixed container.
 * - No logic changes; it just passes through props.
 */
export const EOS_SUPPORT_OPEN_EVENT = 'eos-support-open';

export function SupportWidget({
  apiBase,
  brandColor = '#dc2626',
  zIndex = 9999,
  hideLauncher = false,
}: SupportWidgetProps): JSX.Element {
  // Create or reuse a global container to avoid remounts across route changes
  const mountId = 'eos-support-widget-root';
  const container = useMemo(() => {
    let el = document.getElementById(mountId);
    if (!el) {
      el = document.createElement('div');
      el.id = mountId;
      el.setAttribute('data-eos-widget', 'true');
      Object.assign(el.style, {
        position: 'fixed',
        inset: 'auto 1rem 1rem auto',
        zIndex: String(zIndex),
      });
      document.body.appendChild(el);
    }
    return el;
  }, [zIndex]);

  // Widget state (mimics App.tsx pattern)
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const savedScroll = useRef(0);

  function openPanel(): void {
    setOpen(true);
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = savedScroll.current;
    });
  }

  function closePanel(): void {
    if (scrollRef.current) savedScroll.current = scrollRef.current.scrollTop;
    setOpen(false);
  }

  const toggle = (): void => {
    if (!open) openPanel();
    else closePanel();
  };

  // Apply API base via global fetch interceptor or document-level config
  useEffect(() => {
    if (apiBase && apiBase !== '/api') {
      // Store API base for potential fetch interception
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
      (window as any).__EOS_API_BASE__ = apiBase;
    }
  }, [apiBase]);

  // Apply brand color CSS variable if provided
  useEffect(() => {
    if (container && brandColor) {
      container.style.setProperty('--eos-brand', brandColor);
    }
  }, [container, brandColor]);

  // No scroll-jank: ensures container remains in body
  useEffect(() => {
    return () => {
      // Keep node for route changes; remove only if you truly unmount at app teardown
    };
  }, []);

  // When hideLauncher is true (e.g. Workbench), allow opening via custom event from header button
  useEffect(() => {
    const handler = (): void => {
      setOpen(true);
    };
    window.addEventListener(EOS_SUPPORT_OPEN_EVENT, handler);
    return () => window.removeEventListener(EOS_SUPPORT_OPEN_EVENT, handler);
  }, []);

  return createPortal(
    <div className="min-h-screen bg-white text-zinc-900">
      {/* Floating launcher — hidden on Workbench where Support is in the timeline header */}
      {!hideLauncher && <ChatLauncher open={open} onToggle={toggle} />}

      {/* Chat widget panel — always mounted, just hidden when minimized */}
      <div
        ref={panelRef}
        className={`widget-panel ${
          open ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full pointer-events-none'
        } transition-all duration-300 ease-in-out`}
        aria-hidden={!open}
      >
        <div className="widget-header">
          <div className="font-semibold">Gradcut Support</div>
          <button
            className="text-zinc-600 hover:text-zinc-900"
            onClick={closePanel}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Only this area scrolls */}
        <div ref={scrollRef} className="widget-body">
          <Chat />
        </div>
      </div>
    </div>,
    container,
  );
}
